<template>
  <div class="fixed top-0 left-0 right-0 z-40">
    <div aria-hidden="true" class="slide-in-scrim fixed inset-0 h-screen bg-neutral-900/20" @click="onBack" />
    <div
      ref="panel"
      role="dialog"
      aria-modal="true"
      :aria-label="title || undefined"
      :tabindex="-1"
      class="slide-in-panel fixed top-0 right-0 h-screen overflow-hidden border-l border-neutral-100 bg-white shadow-lg focus:outline-none max-lg:!w-full"
      :style="{ width: `${widthPercentage ?? 80}%` }"
      @keydown.tab="trapFocus"
    >
      <div class="grid h-screen grid-rows-[auto_1fr] gap-2 overflow-hidden px-4 py-4 md:px-8 md:py-7">
        <header class="flex items-center justify-between gap-2">
          <button
            v-if="parentTo"
            type="button"
            :aria-label="t('action.back')"
            class="-ml-1 p-1 text-neutral-600 transition-colors duration-150 ease-out-quart hover:text-neutral-800 md:hidden"
            @click="onBack"
          >
            <font-awesome-icon :icon="['fal', 'chevron-left']" size="lg" />
          </button>

          <slot name="header">
            <div class="min-w-0 flex-1">
              <h1 class="flex items-center text-xl font-bold text-neutral-700">
                <span class="truncate">{{ title }}</span>
                <button
                  v-if="editable && title"
                  v-permission="{ ids: editPermissionIds, hide: false }"
                  type="button"
                  :aria-label="t('action.edit')"
                  class="ml-0.5 shrink-0 cursor-pointer p-1 text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-neutral-600"
                  @click="emit('edit')"
                >
                  <font-awesome-icon :icon="['far', 'pen-to-square']" size="1x" />
                </button>
              </h1>
              <p v-if="subtitle" class="truncate text-sm text-neutral-500">
                {{ subtitle }}
              </p>
            </div>
          </slot>

          <!-- shrink-0: the header title truncates, the action labels must not. -->
          <div class="flex-initial shrink-0">
            <slot name="actions"></slot>
          </div>
          <InfuseIconButton
            v-if="parentTo"
            :icon="['fas', 'xmark']"
            :aria-label="t('action.close')"
            size="lg"
            :to="parentTo"
            class="max-md:hidden"
          />
        </header>

        <main class="h-full overflow-hidden">
          <slot></slot>
        </main>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { InfuseIconButton } from '@infuse/ui/components';
import { RouteLocationRaw, useRouter } from 'vue-router';
import { vPermission } from '@infuse/iam/permission/directives';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onKeyStroke } from '@vueuse/core';

const { t } = useI18n();
const router = useRouter();

// Modal dialog behaviour: move focus into the panel on open, keep Tab
// cycling inside it, and hand focus back to the opener on close.
const panel = ref<HTMLElement>();
let opener: HTMLElement | null = null;

const focusablesInPanel = (): HTMLElement[] =>
  Array.from(
    panel.value?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) ?? []
    // Skip controls that are display:none at the current breakpoint (e.g. the
    // desktop-only close button on mobile) — they must not anchor the trap.
  ).filter((el) => el.getClientRects().length > 0);

function trapFocus(event: KeyboardEvent) {
  const focusables = focusablesInPanel();

  if (focusables.length === 0) {
    event.preventDefault();
    panel.value?.focus();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

onMounted(() => {
  opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  panel.value?.focus();
});

onBeforeUnmount(() => {
  opener?.focus();
});

// Close on Escape — unless a modal is open on top; that one closes first.
onKeyStroke('Escape', () => {
  if (document.getElementById('modalRoot')) {
    return;
  }

  onBack();
});

const props = defineProps<{
  title?: string;
  subtitle?: string;
  parentTo?: RouteLocationRaw;
  widthPercentage?: number;
  editable?: boolean;
  editPermissionId?: string;
}>();

const editPermissionIds = computed(() => {
  return props.editPermissionId ? [props.editPermissionId] : [];
});

const emit = defineEmits<{
  edit(): void;
}>();

function onBack() {
  if (props.parentTo) {
    router.push(props.parentTo);
  }
}
</script>
